import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { css } from '@emotion/css';
import {
  Theme,
  useTheme,
  Button,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { BiArrowBack } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { Customers } from '@gql/base-types';
import { useCreateUserMutation } from '../../queries/createUser.mutation.generated';
import { useGetCurrentUserCustomerIdsQuery } from '../../queries/getCurrentUserCustomerIds.query.generated';
import { useUser, useRooftop } from '@store';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import { ROLE_DEALER_SUPER_ADMIN, ROLE_DRIVER } from '../../utils/roleUtils';
import { useMarketAccess } from '../../hooks/useMarketAccess';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useRoleManagement } from '../../hooks/useRoleManagement';
import { useSingleFlight } from '../../hooks/useSingleFlight';
import { validateEmail } from '@services/validationService';
import { canHaveDrivers } from '@utils/tiers';
import { deriveRooftopEligibility } from '../../utils/featureEligibility';
import PersonStep, { PersonData } from './PersonStep';
import RooftopStep, { RooftopData, deriveRole } from './RooftopStep';
import AccessTypeStep, { AccessData } from './AccessTypeStep';
import FeaturesStep from './FeaturesStep';
import DriverAttributesStep, { type DriverAttributeState } from './DriverAttributesStep';
import ReviewStep from './ReviewStep';

// ---------------------------------------------------------------- //

interface StepDef {
  key: string;
  label: string;
}

// ---------------------------------------------------------------- //

const AddTeamMember = () => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useUser();
  const rooftop = useRooftop();

  // Driver-only mode: scoped flow for driver creation
  const isDriverMode = searchParams.get('type') === 'driver';

  const [createUser] = useCreateUserMutation();
  const { grantMarkets } = useMarketAccess();
  const { grantFeatures } = useFeatureAccess();
  const { grantDriverAccess } = useRoleManagement();

  // Queries
  const userId = currentUser?.profile?.id;

  const { data: currentUserCustomerData } = useGetCurrentUserCustomerIdsQuery({
    variables: { userId: userId || 0 },
    skip: !userId,
  });

  const currentUserCustomers = currentUserCustomerData?.usertocustomers ?? [];
  const currentUserRooftopCount = currentUserCustomers.length;

  // Available customers from the current user's usertocustomers link table
  // In driver mode, only show lite-tier rooftops since drivers require lite
  const availableCustomers = React.useMemo(
    () => {
      const all = currentUserCustomers.map((c: any) => c.customer).filter(Boolean) as Customers[];
      if (isDriverMode) return all.filter((c: any) => canHaveDrivers(c));
      return all;
    },
    [currentUserCustomers, isDriverMode],
  );

  // Step state
  const [activeStep, setActiveStep] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);

  // Form state
  const [person, setPerson] = React.useState<PersonData>({
    displayName: '',
    email: '',
    phone: '',
  });

  const [rooftopData, setRooftopData] = React.useState<RooftopData>({
    rooftopIds: rooftop?.id ? [rooftop.id] : [],
    isSuperAdmin: false,
  });

  const [access, setAccess] = React.useState<AccessData>({
    portalAccess: searchParams.get('type') !== 'driver',
    mobileAccess: searchParams.get('type') === 'driver',
  });

  const [featureIds, setFeatureIds] = React.useState<Set<number>>(new Set());
  const [driverAttributes, setDriverAttributes] = React.useState<DriverAttributeState>({});

  // Anything the dealer typed or picked counts. The rooftop and access type are
  // filled in for them, so those alone do not.
  const holdsUnsavedWork =
    submitting ||
    activeStep > 0 ||
    Object.values(person).some(value => value.trim() !== '') ||
    featureIds.size > 0 ||
    Object.values(driverAttributes).some(Boolean);
  useReloadGuard(holdsUnsavedWork, 'New team member');

  // Validation
  const [personErrors, setPersonErrors] = React.useState<Partial<Record<keyof PersonData, string>>>({});
  const [accessError, setAccessError] = React.useState<string>();
  const [driverPhoneError, setDriverPhoneError] = React.useState<string>();

  // Tiers + enabled services for the rooftops being assigned — single derivation
  // for both the tier gate (+ Lite badge) and the service `requires` gate, so the
  // wizard's Features step gates identically to the user Features tab.
  const { tiers: selectedRooftopTiers, enabledServices: selectedEnabledServices } = React.useMemo(() => {
    const idsToCheck = rooftopData.isSuperAdmin
      ? availableCustomers.map(c => c.id)
      : rooftopData.rooftopIds;
    const rooftops = idsToCheck
      .map(id => availableCustomers.find(c => c.id === id))
      .filter(Boolean)
      .map((c: any) => ({ tier: c.tier, effective_config: c.effective_config, contracts: c.contracts }));
    return deriveRooftopEligibility(rooftops);
  }, [rooftopData.rooftopIds, rooftopData.isSuperAdmin, availableCustomers]);

  // Does the admin have any driver-capable rooftops at all? (lite OR Mobile Service)
  const hasDriverCapableRooftop = React.useMemo(
    () => availableCustomers.some((c: any) => canHaveDrivers(c)),
    [availableCustomers],
  );

  // Determine if mobile can be enabled (at least one selected rooftop is driver-capable)
  const canEnableMobile = React.useMemo(() => {
    if (rooftopData.isSuperAdmin) {
      return availableCustomers.some((c: any) => canHaveDrivers(c));
    }
    return rooftopData.rooftopIds.some(id => {
      const customer = availableCustomers.find(c => c.id === id);
      return canHaveDrivers(customer as any);
    });
  }, [rooftopData.rooftopIds, rooftopData.isSuperAdmin, availableCustomers]);

  // Auto-select portal-only when admin has no lite rooftops (not in driver mode)
  React.useEffect(() => {
    if (!isDriverMode && !hasDriverCapableRooftop) {
      setAccess({ portalAccess: true, mobileAccess: false });
    }
  }, [isDriverMode, hasDriverCapableRooftop]);

  // Auto-deselect mobile access if no lite rooftops remain in selection (not in driver mode)
  React.useEffect(() => {
    if (!isDriverMode && !canEnableMobile && access.mobileAccess) {
      setAccess(prev => ({ ...prev, mobileAccess: false }));
    }
  }, [isDriverMode, canEnableMobile, access.mobileAccess]);

  // Auto-assign single rooftop for admins with 1 rooftop
  React.useEffect(() => {
    if (currentUserRooftopCount === 1 && currentUserCustomers[0]) {
      const singleId = (currentUserCustomers[0] as any).customer?.id;
      if (singleId && rooftopData.rooftopIds.length === 0) {
        setRooftopData(prev => ({ ...prev, rooftopIds: [singleId] }));
      }
    }
  }, [currentUserRooftopCount, currentUserCustomers]);

  // Dynamic step computation
  const steps = React.useMemo(() => {
    const s: StepDef[] = [{ key: 'person', label: 'Person' }];
    if (isDriverMode) {
      // Driver mode: Person → Rooftops (lite only) → Driver Setup → Review
      if (availableCustomers.length > 1) s.push({ key: 'rooftop', label: 'Rooftops' });
      s.push({ key: 'driver', label: 'Driver Setup' });
    } else {
      if (currentUserRooftopCount > 1) s.push({ key: 'rooftop', label: 'Rooftops' });
      if (hasDriverCapableRooftop) s.push({ key: 'access', label: 'Access Type' });
      if (access.portalAccess) s.push({ key: 'features', label: 'Features' });
      if (access.mobileAccess) s.push({ key: 'driver', label: 'Driver Setup' });
    }
    s.push({ key: 'review', label: 'Review' });
    return s;
  }, [isDriverMode, availableCustomers.length, currentUserRooftopCount, hasDriverCapableRooftop, access.portalAccess, access.mobileAccess]);

  // Clamp activeStep when steps array shrinks
  React.useEffect(() => {
    if (activeStep >= steps.length) {
      setActiveStep(steps.length - 1);
    }
  }, [steps.length, activeStep]);

  // Validation
  const validatePerson = (): boolean => {
    const errors: Partial<Record<keyof PersonData, string>> = {};
    if (!person.displayName.trim()) errors.displayName = 'Display name is required';
    if (!person.email.trim()) errors.email = 'Email is required';
    else if (!validateEmail(person.email)) errors.email = 'Invalid email format';
    setPersonErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAccess = (): boolean => {
    if (!access.portalAccess && !access.mobileAccess) {
      setAccessError('At least one access type must be selected');
      return false;
    }
    setAccessError(undefined);
    return true;
  };

  const validateDriver = (): boolean => {
    const digits = person.phone.replace(/\D/g, '');
    // Phone is optional, but if provided it must be a valid 10-digit number
    if (digits.length > 0 && digits.length < 10) {
      setDriverPhoneError('Please enter a valid 10-digit phone number');
      return false;
    }
    setDriverPhoneError(undefined);
    return true;
  };

  const handleNext = () => {
    const currentKey = steps[activeStep]?.key;
    if (currentKey === 'person' && !validatePerson()) return;
    if (currentKey === 'access' && !validateAccess()) return;
    if (currentKey === 'driver' && !validateDriver()) return;
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Submission error displayed below the Create button
  const [submitError, setSubmitError] = React.useState<string>();

  // Synchronous re-entry guard: a rapid double-click can dispatch two onClicks
  // before `setSubmitting(true)` re-renders the button disabled, so a ref-based
  // lock (not the async `submitting` state) is what actually prevents a duplicate
  // createUser. `submitting` state remains for the button's disabled/spinner UI.
  const { run: handleSubmit } = useSingleFlight(async () => {
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const role = (!access.portalAccess && access.mobileAccess) ? ROLE_DRIVER : deriveRole(rooftopData);
      const now = new Date().toISOString();

      const userObj: any = {
        display_name: person.displayName.trim(),
        email: person.email.trim().toLowerCase(),
        phone: person.phone.trim() || null,
        default_role: role,
        customer_id: rooftop?.id,
        active: true,
        created_at: now,
        updated_at: now,
      };

      const result = await createUser({ variables: { userObj } });
      const newUserId = result.data?.insert_users?.returning?.[0]?.id;

      if (!newUserId) {
        setSubmitError('Failed to create user — no ID returned.');
        setSubmitting(false);
        return;
      }

      // Grant market access
      if (role !== ROLE_DEALER_SUPER_ADMIN && rooftopData.rooftopIds.length > 0) {
        await grantMarkets(newUserId, rooftopData.rooftopIds);
      }

      // Grant selected features
      if (access.portalAccess && featureIds.size > 0) {
        await grantFeatures(newUserId, Array.from(featureIds));
      }

      // Create driver record if mobile access is selected
      if (access.mobileAccess) {
        await grantDriverAccess(newUserId, false, driverAttributes);
      }

      toast.success(isDriverMode ? 'Driver created successfully!' : 'User created successfully!');
      navigate(`/users/${newUserId}`);
    } catch (err: any) {
      console.error('Error creating user:', err);

      // Extract a user-friendly message
      const raw = err?.message || '';
      const gqlErrors = err?.graphQLErrors ?? [];
      const firstGql = gqlErrors[0]?.message ?? '';

      let friendly: string;
      if (raw.includes('Uniqueness violation') || firstGql.includes('Uniqueness violation')) {
        friendly = 'A user with this email already exists.';
      } else if (raw.includes('email-already-in-use') || firstGql.includes('already exists')) {
        friendly = 'This email is already associated with an account.';
      } else if (firstGql) {
        friendly = firstGql;
      } else if (raw) {
        friendly = raw;
      } else {
        friendly = 'Failed to create user. Please try again.';
      }

      setSubmitError(friendly);
      toast.error(friendly);
      setSubmitting(false);
    }
  });

  // Customer name lookup for ReviewStep
  const customerNames = React.useMemo(() => {
    const map = new Map<number, string>();
    for (const c of availableCustomers) {
      map.set(c.id, c.name || '');
    }
    return map;
  }, [availableCustomers]);

  const renderStepContent = () => {
    const currentKey = steps[activeStep]?.key;
    switch (currentKey) {
      case 'person':
        return <PersonStep data={person} onChange={setPerson} errors={personErrors} />;
      case 'rooftop':
        return (
          <RooftopStep
            data={rooftopData}
            onChange={setRooftopData}
            availableCustomers={availableCustomers}
            currentUserRole={currentUser?.profile?.default_role ?? undefined}
            hideSuperAdmin={isDriverMode}
          />
        );
      case 'access':
        return (
          <AccessTypeStep
            data={access}
            onChange={setAccess}
            error={accessError}
            canEnableMobile={canEnableMobile}
          />
        );
      case 'features':
        return <FeaturesStep selectedFeatureIds={featureIds} onChange={setFeatureIds} targetUserTiers={selectedRooftopTiers} enabledServices={selectedEnabledServices} />;
      case 'driver':
        return (
          <DriverAttributesStep
            attributes={driverAttributes}
            onChange={setDriverAttributes}
            selectedCustomerIds={rooftopData.rooftopIds}
            phone={person.phone}
            onPhoneChange={phone => setPerson(prev => ({ ...prev, phone }))}
            phoneError={driverPhoneError}
          />
        );
      case 'review':
        return (
          <ReviewStep
            person={person}
            access={access}
            rooftop={rooftopData}
            featureIds={featureIds}
            driverAttributes={driverAttributes}
            orgCustomerNames={customerNames}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm" className={cls.container}>
      {/* Back link */}
      <Button
        variant="text"
        startIcon={<BiArrowBack />}
        onClick={() => navigate('/users')}
        sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
      >
        Back to Users
      </Button>

      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        {isDriverMode ? 'Add Driver' : 'Add User'}
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} className={cls.stepper} alternativeLabel>
        {steps.map(step => (
          <Step key={step.key}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step content */}
      <Paper variant="outlined" className={cls.stepContent}>
        {renderStepContent()}
      </Paper>

      {/* Error banner */}
      {submitError && (
        <Typography variant="body2" color="error" sx={{ px: 1, pb: 1 }}>
          {submitError}
        </Typography>
      )}

      {/* Sticky footer */}
      <div className={cls.footer}>
        <Button
          variant="text"
          onClick={() => navigate('/users')}
          sx={{ textTransform: 'none' }}
        >
          Discard
        </Button>

        <Box display="flex" gap={1.5}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={submitting}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              sx={{ textTransform: 'none' }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ textTransform: 'none', minWidth: 100 }}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Create'}
            </Button>
          )}
        </Box>
      </div>
    </Container>
  );
};

// ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  container: css`
    padding-top: 24px;
    padding-bottom: 24px;
  `,
  stepper: css`
    margin-bottom: 24px;
  `,
  stepContent: css`
    padding: 32px;
    border-radius: 12px;
    margin-bottom: 24px;
  `,
  footer: css`
    position: sticky;
    bottom: 0;
    background-color: ${theme?.palette?.background?.paper};
    border-top: 1px solid ${theme?.palette?.divider};
    padding: 16px 24px;
    margin-left: -24px;
    margin-right: -24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
});

export default AddTeamMember;
